import { isScenarioEligible } from "../../logic/decisionEngine";
import { useEffect, useMemo, useState } from "react";
import { CarScreenViewport } from "./CarScreenViewport";
import { NightAccidentGuideOverlay } from "./NightAccidentGuideOverlay";
import { NightAiCard } from "./NightAiCard";
import { NightAiExpandedCard } from "./NightAiExpandedCard";
import { NightAudioCompanionDemo } from "./NightAudioCompanionDemo";
import { NightBottomCards } from "./NightBottomCards";
import { NightCarwashModeCard } from "./NightCarwashModeCard";
import { NightChargingSessionCard } from "./NightChargingSessionCard";
import { NightDock } from "./NightDock";
import { NightFloatingShortcut } from "./NightFloatingShortcut";
import { NightHeroLayer } from "./NightHeroLayer";
import { NightParcelExitReminderCard } from "./NightParcelExitReminderCard";
import { NightRefuelAuthorizationCard } from "./NightRefuelAuthorizationCard";
import { NightStatusBar, NightVoiceCaptionOverlay } from "./NightStatusBar";
import { NightTomatoReactionDemo } from "./NightTomatoReactionDemo";
import { NightMovieGachaDemo } from "./NightMovieGachaDemo";
import { NightVideoCompanionLayer } from "./NightVideoCompanionLayer";
import { NightXiaohongshuParkingDemo } from "./NightXiaohongshuParkingDemo";
import { NightEvaSpaceDemo } from "./NightEvaSpaceDemo";
import { ParcelDetailPopup } from "./ParcelDetailPopup";
import { TraditionalDesktopAmapLayer } from "./TraditionalDesktopAmapLayer";
import { TraditionalDesktopWaitingRouteCard } from "./TraditionalDesktopWaitingRouteCard";
import {
  type TraditionalDesktopDestinationTarget
} from "./TraditionalDesktopMapDestination";
import {
  TraditionalDesktopCarwashNavigationCard,
  TraditionalDesktopParcelNavigationCard,
  TraditionalDesktopRefuelNavigationCard,
  TraditionalDesktopMapNavigationCard,
  type TraditionalCarwashFlowStage,
  type TraditionalChargingFlowStage,
  type TraditionalParcelFlowStage,
  type TraditionalRefuelFlowStage
} from "./TraditionalDesktopMapNavigationCard";
import {
  FALLBACK_CHARGING_STATIONS,
  type ChargingStation,
  type GeoPoint,
  getRecommendedStation
} from "../../config/amapChargingDemo";
import {
  getRecommendedCarwashShop,
  type CarwashShop,
} from "../../config/amapCarwashDemo";
import {
  getRecommendedFuelStation,
  type FuelStation
} from "../../config/amapFuelDemo";
import type { CarwashModeState } from "../../config/carwashMode";
import {
  getPickupPointDisplayName,
  getRecommendedParcelPickupPoint,
} from "../../config/amapParcelDemo";
import type { ChargingSessionState } from "../../config/chargingSession";
import type {
  AudioCompanionDemoState,
  AudioCompanionProgramId
} from "../../config/audioCompanionDemo";
import type {
  VideoCompanionDemoState,
  VideoCompanionVideoId
} from "../../config/videoCompanionDemo";
import {
  LONGYAO_WAITING_DEMO_ORIGIN,
  LONGYAO_WAITING_DESTINATION,
  WAITING_ROUTE_DEFAULT_BAND_ID,
  type WaitingRouteBandId,
  type WaitingRouteDemoState
} from "../../config/amapWaitingRouteDemo";
import type { ParcelExitReminderState } from "../../config/parcelExitReminder";
import type { RefuelAuthorizationState } from "../../config/refuelAuthorization";
import {
  type CockpitData,
  useCockpitData,
  useCockpitRouteGeometry
} from "../../context/CockpitDataContext";
import { NIGHT_ASSETS } from "../../config/figmaNightLayout";
import { useSystemClock } from "../../hooks/useSystemClock";
import { useTimedDetourRoute } from "../../hooks/useTimedDetourRoute";
import type { VoiceDestinationSearchRequest } from "../../logic/voiceCommand";
import type { CabinState, DecisionSummary, ServiceId } from "../../types/domain";
import { assetUrl } from "../../utils/assetUrl";

const EMPTY_CHARGING_STATIONS: ChargingStation[] = [];
const noop = () => undefined;

export interface ParcelPresentationControls {
  flowStage: TraditionalParcelFlowStage;
  onFlowStageChange: (stage: TraditionalParcelFlowStage) => void;
  detailOpen: boolean;
  onDetailOpenChange: (open: boolean) => void;
  selectedPointId: string;
  onSelectedPointChange: (id: string) => void;
}

export function DesktopStage({
  state,
  summary,
  selectedService,
  voiceDestinationSearchRequest,
  voiceCaption,
  audioCompanionDemo,
  videoCompanionDemo,
  waitingRouteDemo,
  carwashMode,
  chargingSession,
  parcelExitReminder,
  parcelPresentation,
  parkingPaymentOpen,
  refuelAuthorization,
  accidentGuideOpen,
  accidentCallVisible,
  onOpenService,
  onCloseService,
  onConfirmAccidentGuide,
  onHangupAccidentCall,
  onCarwashArriveShop,
  onAuthorizeRefuel,
  onConfirmCarwashMode,
  onConfirmService,
  onConfirmChargingCover,
  onDismissCarwashMode,
  onDismissChargingSession,
  onDismissParcelExitReminder,
  onDismissRefuelAuthorization,
  onEnableChargingRestMode,
  onParcelArriveHome,
  onRefuelArriveStation,
  onAudioCompanionAnswerFinished,
  onAudioCompanionBackToRecommend,
  onAudioCompanionExpandPreview,
  onAudioCompanionFallbackSpeak,
  onAudioCompanionPreviewProgram,
  onAudioCompanionSelectProgram,
  onAudioCompanionStartGeneratedProgram,
  onVideoCompanionActorFinished,
  onVideoCompanionAskActor,
  onVideoCompanionOpenQuicklook,
  onVideoCompanionPlaybackEnded,
  onVideoCompanionReset,
  onVideoCompanionSelectVideo,
  onMovieGachaClose,
  onTomatoReactionClose,
  onEvaSpaceClose,
  onEvaSpaceOpenAudioCompanion,
  onEvaSpaceOpenVideoCompanion,
  onWaitingRouteBandChange,
  onShowChargingRestRecommendation,
  onStartChargingSession,
  onCloseParkingPayment,
  onParkingPaymentPaid
}: {
  state: CabinState;
  summary: DecisionSummary;
  selectedService: ServiceId | null;
  voiceDestinationSearchRequest: VoiceDestinationSearchRequest | null;
  voiceCaption?: {
    active: boolean;
    phase: string;
    text: string;
  };
  audioCompanionDemo: AudioCompanionDemoState | null;
  videoCompanionDemo: VideoCompanionDemoState | null;
  waitingRouteDemo: WaitingRouteDemoState | null;
  carwashMode: CarwashModeState;
  chargingSession: ChargingSessionState;
  parcelExitReminder: ParcelExitReminderState;
  parcelPresentation?: ParcelPresentationControls;
  parkingPaymentOpen: boolean;
  refuelAuthorization: RefuelAuthorizationState;
  accidentGuideOpen: boolean;
  accidentCallVisible: boolean;
  onOpenService: (serviceId: ServiceId) => void;
  onCloseService: () => void;
  onConfirmAccidentGuide: () => void;
  onHangupAccidentCall: () => void;
  onCarwashArriveShop: (shop: CarwashShop) => void;
  onAuthorizeRefuel: () => void;
  onConfirmCarwashMode: () => void;
  onConfirmService: () => void;
  onConfirmChargingCover: () => void;
  onDismissCarwashMode: () => void;
  onDismissChargingSession: () => void;
  onDismissParcelExitReminder: () => void;
  onDismissRefuelAuthorization: () => void;
  onEnableChargingRestMode: () => void;
  onParcelArriveHome: (payload: { pendingCount: number; pickupPointName: string }) => void;
  onRefuelArriveStation: (station: FuelStation) => void;
  onAudioCompanionAnswerFinished: () => void;
  onAudioCompanionBackToRecommend: () => void;
  onAudioCompanionExpandPreview: () => void;
  onAudioCompanionFallbackSpeak: (text: string) => void;
  onAudioCompanionPreviewProgram: (programId: AudioCompanionProgramId) => void;
  onAudioCompanionSelectProgram: (programId: AudioCompanionProgramId) => void;
  onAudioCompanionStartGeneratedProgram: () => void;
  onVideoCompanionActorFinished: () => void;
  onVideoCompanionAskActor: () => void;
  onVideoCompanionOpenQuicklook: () => void;
  onVideoCompanionPlaybackEnded: () => void;
  onVideoCompanionReset: () => void;
  onVideoCompanionSelectVideo: (videoId: VideoCompanionVideoId, nextStage?: VideoCompanionDemoState["stage"]) => void;
  onMovieGachaClose: () => void;
  onTomatoReactionClose: () => void;
  onEvaSpaceClose: () => void;
  onEvaSpaceOpenAudioCompanion: () => void;
  onEvaSpaceOpenVideoCompanion: () => void;
  onWaitingRouteBandChange: (bandId: WaitingRouteBandId) => void;
  onShowChargingRestRecommendation: () => void;
  onStartChargingSession: (station: ChargingStation) => void;
  onCloseParkingPayment: () => void;
  onParkingPaymentPaid: () => void;
}) {
  const [localParcelDetailOpen, setLocalParcelDetailOpen] = useState(false);
  const parcelDetailOpen = parcelPresentation?.detailOpen ?? localParcelDetailOpen;
  const setParcelDetailOpen = parcelPresentation?.onDetailOpenChange ?? setLocalParcelDetailOpen;
  const [traditionalCardCollapsed, setTraditionalCardCollapsed] = useState(false);
  const [traditionalDestinationTarget, setTraditionalDestinationTarget] =
    useState<TraditionalDesktopDestinationTarget>("restaurant");
  const [traditionalChargingFlowStage, setTraditionalChargingFlowStage] =
    useState<TraditionalChargingFlowStage>("selection");
  const [selectedChargingStationId, setSelectedChargingStationId] = useState(FALLBACK_CHARGING_STATIONS[0].id);
  const [chargingStationManuallySelected, setChargingStationManuallySelected] = useState(false);
  const [traditionalCarwashCardCollapsed, setTraditionalCarwashCardCollapsed] = useState(false);
  const [traditionalCarwashFlowStage, setTraditionalCarwashFlowStage] =
    useState<TraditionalCarwashFlowStage>("selection");
  const [selectedCarwashShopId, setSelectedCarwashShopId] = useState("");
  const [carwashShopManuallySelected, setCarwashShopManuallySelected] = useState(false);
  const [traditionalRefuelCardCollapsed, setTraditionalRefuelCardCollapsed] = useState(false);
  const [traditionalRefuelFlowStage, setTraditionalRefuelFlowStage] =
    useState<TraditionalRefuelFlowStage>("selection");
  const [selectedFuelStationId, setSelectedFuelStationId] = useState("");
  const [fuelStationManuallySelected, setFuelStationManuallySelected] = useState(false);
  const [localTraditionalParcelCardCollapsed, setTraditionalParcelCardCollapsed] = useState(false);
  const [localTraditionalParcelFlowStage, setLocalTraditionalParcelFlowStage] =
    useState<TraditionalParcelFlowStage>("selection");
  const traditionalParcelFlowStage = parcelPresentation?.flowStage ?? localTraditionalParcelFlowStage;
  const setTraditionalParcelFlowStage = parcelPresentation?.onFlowStageChange ?? setLocalTraditionalParcelFlowStage;
  const traditionalParcelCardCollapsed = parcelPresentation
    ? traditionalParcelFlowStage === "toPickup" || traditionalParcelFlowStage === "toHome"
    : localTraditionalParcelCardCollapsed;
  const [localSelectedParcelPointId, setLocalSelectedParcelPointId] = useState("");
  const selectedParcelPointId = parcelPresentation?.selectedPointId ?? localSelectedParcelPointId;
  const setSelectedParcelPointId = parcelPresentation?.onSelectedPointChange ?? setLocalSelectedParcelPointId;
  const [destinationSearchServiceId, setDestinationSearchServiceId] = useState<ServiceId | null>(null);
  const cockpitData = useCockpitData();
  const destinationName = cockpitData.charging.destination.name;
  const destinationLocation = cockpitData.charging.destination.location;
  const carwashShops = cockpitData.carwash.shops;
  const carwashShopSource = cockpitData.carwash.source;
  const chargingStations = cockpitData.charging.stations;
  const chargingStationSource = cockpitData.charging.source;
  const fuelStations = cockpitData.fuel.stations;
  const fuelStationSource = cockpitData.fuel.source;
  const parcelPickupPoints = cockpitData.parcel.pickupPoints;
  const parcelPointSource = cockpitData.parcel.source;
  const selectedParcelPoint =
    parcelPickupPoints.find((point) => point.id === selectedParcelPointId) ??
    cockpitData.parcel.pickupPoint;
  const selectedCarwashShop =
    carwashShops.find((shop) => shop.id === selectedCarwashShopId) ??
    cockpitData.carwash.recommendedShop;
  const selectedFuelStation =
    fuelStations.find((station) => station.id === selectedFuelStationId) ??
    cockpitData.fuel.recommendedStation;
  const isWaitingRouteVisible = Boolean(waitingRouteDemo && state.desktop === "standard");
  const waitingRouteVehicle = useMemo(
    () => createStationVehicleState(cockpitData.vehicle, LONGYAO_WAITING_DEMO_ORIGIN.location),
    [cockpitData.vehicle]
  );
  const waitingRoutePlan = useTimedDetourRoute({
    bandId: waitingRouteDemo?.bandId ?? WAITING_ROUTE_DEFAULT_BAND_ID,
    enabled: isWaitingRouteVisible,
    origin: LONGYAO_WAITING_DEMO_ORIGIN.location,
    revision: waitingRouteDemo?.revision ?? 0
  });
  const systemTime = useSystemClock();
  const isAudioCompanionVisible = Boolean(audioCompanionDemo && state.desktop === "ai");
  const isVideoCompanionVisible = Boolean(videoCompanionDemo && state.scriptTrigger === "video-companion" && state.desktop === "ai");
  const isMovieGachaVisible = state.scriptTrigger === "movie-gacha" && state.desktop === "ai";
  const isTomatoReactionVisible = state.scriptTrigger === "tomato-reaction" && state.desktop === "ai";
  const isXiaohongshuParkingVisible = state.scriptTrigger === "xiaohongshu-parking" && state.desktop === "ai";
  const isEvaSpaceVisible = state.scriptTrigger === "eva-space" && state.desktop === "ai";
  const isCompanionVisible = isAudioCompanionVisible || isVideoCompanionVisible || isMovieGachaVisible || isTomatoReactionVisible || isXiaohongshuParkingVisible || isEvaSpaceVisible;
  const displayedTime = parcelPresentation
    ? "18:30"
    : isAudioCompanionVisible
    ? "08:15"
    : isVideoCompanionVisible
      ? "18:40"
      : isMovieGachaVisible
        ? "20:15"
      : isTomatoReactionVisible
        ? "18:40"
      : isXiaohongshuParkingVisible
        ? "09:30"
        : isEvaSpaceVisible
          ? "20:24"
        : systemTime;
  const wallpaperSrc = assetUrl(NIGHT_ASSETS.wallpaper);
  const topDockSrc = assetUrl(NIGHT_ASSETS.topDock);
  const avatarSrc = assetUrl(NIGHT_ASSETS.avatar);
  const floatingShortcutSrc = assetUrl(NIGHT_ASSETS.floatingShortcut);
  const traditionalDesktopSrc = assetUrl(NIGHT_ASSETS.traditionalDesktop);
  const traditionalDockSrc = assetUrl(NIGHT_ASSETS.traditionalDock);
  const traditionalCarwashNavigationSrc = assetUrl(NIGHT_ASSETS.traditionalCarwashNavigation);
  const traditionalParcelNavigationSrc = assetUrl(NIGHT_ASSETS.traditionalParcelNavigation);
  const traditionalChargingNavigationSrc = assetUrl(NIGHT_ASSETS.traditionalChargingNavigation);
  const evaFrontSrc = assetUrl(NIGHT_ASSETS.evaFront);
  const evaLookDownSrc = assetUrl(NIGHT_ASSETS.evaLookDown);
  const evaAlertSrc = assetUrl(NIGHT_ASSETS.evaStand);
  const parkingCardSrc = assetUrl(NIGHT_ASSETS.parkingCard);
  const musicCardSrc = assetUrl(NIGHT_ASSETS.musicCard);
  const selectedDecision = selectedService
    ? summary.decisions.find((decision) => decision.scenario.id === selectedService && isScenarioEligible(state, decision.scenario)) ?? null
    : null;
  const isChargingSessionVisible = chargingSession.stage !== "idle";
  const isCarwashModeVisible = carwashMode.stage !== "idle";
  const isParcelExitReminderVisible = parcelExitReminder.visible;
  const isRefuelAuthorizationVisible = refuelAuthorization.stage !== "idle";
  const hasNightPrompt = Boolean(isCompanionVisible || summary.activeDecision || selectedDecision || isCarwashModeVisible || isChargingSessionVisible || isParcelExitReminderVisible || isRefuelAuthorizationVisible || accidentGuideOpen);
  const isNightCardExpanded = Boolean(isCompanionVisible || selectedDecision || isCarwashModeVisible || isChargingSessionVisible || isParcelExitReminderVisible || isRefuelAuthorizationVisible || accidentGuideOpen);
  const activeServiceId = summary.activeDecision?.scenario.id ?? (state.desktop === "standard" ? selectedService : null);
  const shouldShowTraditionalRefuelCard = activeServiceId === "refuel" && (state.fuelLevel <= 30 || state.fuelRangeKm <= 240);
  const traditionalNavigationSrc = getTraditionalNavigationSrc({
    activeServiceId,
    carwashNavigationSrc: traditionalCarwashNavigationSrc,
    chargingNavigationSrc: traditionalChargingNavigationSrc,
    hasCarwashInput: state.carwashCoupon,
    hasChargingRoute: state.batteryLevel <= 28 && state.chargerAvailability >= 4,
    hasParcelRoute: state.parcelPending && state.parcelOnRoute,
    parcelNavigationSrc: traditionalParcelNavigationSrc
  });
  const shouldShowTraditionalChargingCard = !shouldShowTraditionalRefuelCard && traditionalNavigationSrc === traditionalChargingNavigationSrc;
  const shouldShowTraditionalCarwashCard = !shouldShowTraditionalRefuelCard && traditionalNavigationSrc === traditionalCarwashNavigationSrc;
  const shouldShowTraditionalParcelCard = !shouldShowTraditionalRefuelCard && traditionalNavigationSrc === traditionalParcelNavigationSrc;
  const hasTraditionalNavigation = Boolean(
    isWaitingRouteVisible || traditionalNavigationSrc || shouldShowTraditionalRefuelCard
  );
  const hasDestinationSearchRoute =
    state.desktop === "standard" &&
    destinationSearchServiceId === activeServiceId;
  const selectedChargingStation =
    chargingStations.find((station) => station.id === selectedChargingStationId) ??
    cockpitData.charging.recommendedStation;
  const routeStartsAtChargingStation =
    traditionalChargingFlowStage === "arrivedCharging" ||
    traditionalChargingFlowStage === "toDestination";
  const isParcelPresentationHomeRoute = Boolean(parcelPresentation && traditionalParcelFlowStage === "toHome");
  const isParcelPresentationAtPickup = Boolean(parcelPresentation && (
    traditionalParcelFlowStage === "arrivedPickup" || traditionalParcelFlowStage === "toHome"
  ));
  const routeDestinationTarget: TraditionalDesktopDestinationTarget = isParcelPresentationHomeRoute
    ? "restaurant"
    : routeStartsAtChargingStation
    ? "restaurant"
    : hasDestinationSearchRoute
      ? "restaurant"
    : shouldShowTraditionalParcelCard
      ? "parcel"
      : shouldShowTraditionalCarwashCard
        ? "carwash"
      : shouldShowTraditionalRefuelCard
        ? "refuel"
      : traditionalDestinationTarget;
  const routeOriginOverride = isParcelPresentationHomeRoute
    ? selectedParcelPoint.location
    : routeStartsAtChargingStation ? selectedChargingStation.location : undefined;
  const isLiveTraditionalRouteVisible =
    state.desktop === "standard" &&
    !isWaitingRouteVisible &&
    (
      shouldShowTraditionalChargingCard ||
      shouldShowTraditionalCarwashCard ||
      shouldShowTraditionalRefuelCard ||
      shouldShowTraditionalParcelCard
    );
  const routeGeometry = useCockpitRouteGeometry({
    enabled: isLiveTraditionalRouteVisible,
    destinationOverride: isParcelPresentationHomeRoute ? cockpitData.home.location : undefined,
    originOverride: routeOriginOverride,
    selectedCarwashShop,
    selectedFuelStation,
    selectedParcelPoint,
    selectedStation: selectedChargingStation,
    target: routeDestinationTarget
  });
  const displayedVehicle = isParcelPresentationAtPickup
    ? createStationVehicleState(cockpitData.vehicle, selectedParcelPoint.location)
    : routeStartsAtChargingStation
    ? createStationVehicleState(cockpitData.vehicle, selectedChargingStation.location)
    : cockpitData.vehicle;

  useEffect(() => {
    if (parcelPresentation) {
      return;
    }
    if (state.desktop !== "ai" || state.liteMode || (activeServiceId !== "parcel" && selectedService !== "parcel")) {
      setParcelDetailOpen(false);
    }
  }, [activeServiceId, selectedService, state.desktop, state.liteMode]);

  useEffect(() => {
    if (!shouldShowTraditionalChargingCard) {
      setTraditionalCardCollapsed(false);
      setTraditionalDestinationTarget("restaurant");
      setTraditionalChargingFlowStage("selection");
      setSelectedChargingStationId(cockpitData.charging.recommendedStation.id);
      setChargingStationManuallySelected(false);
    }
  }, [cockpitData.charging.recommendedStation.id, shouldShowTraditionalChargingCard]);

  useEffect(() => {
    if (!shouldShowTraditionalCarwashCard) {
      setTraditionalCarwashCardCollapsed(false);
      setTraditionalCarwashFlowStage("selection");
      setSelectedCarwashShopId(cockpitData.carwash.recommendedShop.id);
      setCarwashShopManuallySelected(false);
    }
  }, [cockpitData.carwash.recommendedShop.id, shouldShowTraditionalCarwashCard]);

  useEffect(() => {
    if (!shouldShowTraditionalRefuelCard) {
      setTraditionalRefuelCardCollapsed(false);
      setTraditionalRefuelFlowStage("selection");
      setSelectedFuelStationId(cockpitData.fuel.recommendedStation.id);
      setFuelStationManuallySelected(false);
    }
  }, [cockpitData.fuel.recommendedStation.id, shouldShowTraditionalRefuelCard]);

  useEffect(() => {
    if (!shouldShowTraditionalParcelCard) {
      setTraditionalParcelCardCollapsed(false);
      if (!parcelPresentation) {
        setTraditionalParcelFlowStage("selection");
      }
      setSelectedParcelPointId(cockpitData.parcel.pickupPoint.id);
    }
  }, [cockpitData.parcel.pickupPoint.id, shouldShowTraditionalParcelCard]);

  useEffect(() => {
    const recommendedStationId = getRecommendedStation(chargingStations).id;

    if (chargingStationManuallySelected && chargingStations.some((station) => station.id === selectedChargingStationId)) {
      return;
    }

    setSelectedChargingStationId(recommendedStationId);
    setChargingStationManuallySelected(false);
  }, [chargingStationManuallySelected, chargingStations, selectedChargingStationId]);

  useEffect(() => {
    const recommendedShopId = getRecommendedCarwashShop(carwashShops).id;

    if (carwashShopManuallySelected && carwashShops.some((shop) => shop.id === selectedCarwashShopId)) {
      return;
    }

    setSelectedCarwashShopId(recommendedShopId);
    setCarwashShopManuallySelected(false);
  }, [carwashShopManuallySelected, carwashShops, selectedCarwashShopId]);

  useEffect(() => {
    if (parcelPickupPoints.some((point) => point.id === selectedParcelPointId)) {
      return;
    }

    setSelectedParcelPointId(getRecommendedParcelPickupPoint(parcelPickupPoints).id);
  }, [parcelPickupPoints, selectedParcelPointId]);

  useEffect(() => {
    const recommendedStationId = getRecommendedFuelStation(fuelStations).id;

    if (fuelStationManuallySelected && fuelStations.some((station) => station.id === selectedFuelStationId)) {
      return;
    }

    setSelectedFuelStationId(recommendedStationId);
    setFuelStationManuallySelected(false);
  }, [fuelStationManuallySelected, fuelStations, selectedFuelStationId]);

  useEffect(() => {
    if (state.desktop !== "standard") {
      setDestinationSearchServiceId(null);
      setTraditionalChargingFlowStage("selection");
      setTraditionalCardCollapsed(false);
      setTraditionalDestinationTarget("restaurant");
      setChargingStationManuallySelected(false);
      setTraditionalCarwashFlowStage("selection");
      setTraditionalCarwashCardCollapsed(false);
      setCarwashShopManuallySelected(false);
      setTraditionalRefuelFlowStage("selection");
      setTraditionalRefuelCardCollapsed(false);
      setFuelStationManuallySelected(false);
      if (!parcelPresentation) {
        setTraditionalParcelFlowStage("selection");
      }
      setTraditionalParcelCardCollapsed(false);
    }
  }, [state.desktop]);

  useEffect(() => {
    if (state.desktop !== "standard" || !voiceDestinationSearchRequest) {
      return;
    }

    setDestinationSearchServiceId(voiceDestinationSearchRequest.serviceId);

    if (voiceDestinationSearchRequest.serviceId === "charging") {
      setTraditionalDestinationTarget("restaurant");
      setTraditionalChargingFlowStage("selection");
      setTraditionalCardCollapsed(false);
    }

    if (voiceDestinationSearchRequest.serviceId === "carwash") {
      setTraditionalCarwashFlowStage("selection");
      setTraditionalCarwashCardCollapsed(false);
    }

    if (voiceDestinationSearchRequest.serviceId === "refuel") {
      setTraditionalRefuelFlowStage("selection");
      setTraditionalRefuelCardCollapsed(false);
    }
  }, [state.desktop, voiceDestinationSearchRequest]);

  if (state.desktop === "standard") {
    return (
      <main className="desktop-stage desktop-stage--standard desktop-stage--figma-replica">
        <CarScreenViewport>
          <div className="desktop-stage__screen-canvas desktop-stage__screen-canvas--traditional">
            <div className="traditional-desktop-layer" aria-hidden="true">
              <img
                className="traditional-desktop-layer__source"
                src={traditionalDesktopSrc}
                alt=""
                draggable={false}
              />
            </div>
            {hasTraditionalNavigation ? (
              <div className="traditional-desktop-map-overlay" aria-label="传统桌面地图导航">
                {!isWaitingRouteVisible && !shouldShowTraditionalChargingCard && !shouldShowTraditionalParcelCard && !shouldShowTraditionalCarwashCard && !shouldShowTraditionalRefuelCard && traditionalNavigationSrc ? (
                  <img
                    className="traditional-desktop-map-overlay__image"
                    src={traditionalNavigationSrc}
                    alt=""
                    draggable={false}
                    aria-hidden="true"
                  />
                ) : null}
                {isWaitingRouteVisible && waitingRouteDemo ? (
                  <>
                    <TraditionalDesktopAmapLayer
                      destinationLocation={LONGYAO_WAITING_DESTINATION.location}
                      destinationName={LONGYAO_WAITING_DESTINATION.name}
                      mode="waiting"
                      routeGeometry={waitingRoutePlan.geometry}
                      stations={EMPTY_CHARGING_STATIONS}
                      selectedStation={selectedChargingStation}
                      source={waitingRoutePlan.status === "loading"
                        ? "loading"
                        : waitingRoutePlan.geometry.source === "amap"
                          ? "amap"
                          : "demo"}
                      target="restaurant"
                      vehicle={waitingRouteVehicle}
                      waitingRouteDataSource={waitingRoutePlan.dataSource}
                      onRefreshVehicleLocation={noop}
                      onSelectStation={noop}
                    />
                    <TraditionalDesktopWaitingRouteCard
                      bandId={waitingRouteDemo.bandId}
                      onBandChange={onWaitingRouteBandChange}
                      plan={waitingRoutePlan}
                    />
                  </>
                ) : null}
                {!isWaitingRouteVisible && shouldShowTraditionalChargingCard ? (
                  <>
                    <TraditionalDesktopAmapLayer
                      destinationLocation={destinationLocation}
                      destinationName={destinationName}
                      mode="charging"
                      routeGeometry={routeGeometry}
                      stations={chargingStations}
                      selectedStation={selectedChargingStation}
                      source={chargingStationSource}
                      target={routeDestinationTarget}
                      vehicle={displayedVehicle}
                      onRefreshVehicleLocation={cockpitData.vehicle.refreshLocation}
                      onSelectStation={(stationId) => {
                        setSelectedChargingStationId(stationId);
                        setChargingStationManuallySelected(true);
                        setTraditionalDestinationTarget("charging");
                        setTraditionalChargingFlowStage("selection");
                        setTraditionalCardCollapsed(false);
                      }}
                    />
                    <TraditionalDesktopMapNavigationCard
                      batteryLevel={state.batteryLevel}
                      collapsed={traditionalCardCollapsed}
                      destinationName={destinationName}
                      flowStage={traditionalChargingFlowStage}
                      selectedStation={selectedChargingStation}
                      selectedStationId={selectedChargingStationId}
                      stations={chargingStations}
                      target={routeDestinationTarget}
                      onArriveCharging={() => {
                        setTraditionalDestinationTarget("charging");
                        setTraditionalChargingFlowStage("arrivedCharging");
                        setTraditionalCardCollapsed(false);
                      }}
                      onContinueDestination={() => {
                        setTraditionalDestinationTarget("restaurant");
                        setTraditionalChargingFlowStage("toDestination");
                        setTraditionalCardCollapsed(true);
                      }}
                      onExpand={() => {
                        setTraditionalCardCollapsed(false);
                        if (traditionalChargingFlowStage !== "arrivedCharging") {
                          setTraditionalChargingFlowStage("selection");
                        }
                      }}
                      onGoCharge={() => {
                        setTraditionalDestinationTarget("charging");
                        setTraditionalChargingFlowStage("toCharging");
                        setTraditionalCardCollapsed(true);
                        onStartChargingSession(selectedChargingStation);
                      }}
                      onGoDestination={() => {
                        setTraditionalDestinationTarget("restaurant");
                        setTraditionalChargingFlowStage("directDestination");
                        setTraditionalCardCollapsed(true);
                      }}
                      onSelectStation={(stationId) => {
                        setSelectedChargingStationId(stationId);
                        setChargingStationManuallySelected(true);
                        setTraditionalDestinationTarget("charging");
                        setTraditionalChargingFlowStage("selection");
                        setTraditionalCardCollapsed(false);
                      }}
                    />
                  </>
                ) : null}
                {!isWaitingRouteVisible && shouldShowTraditionalCarwashCard ? (
                  <>
                    <TraditionalDesktopAmapLayer
                      destinationLocation={destinationLocation}
                      destinationName={destinationName}
                      mode="carwash"
                      carwashShops={carwashShops}
                      selectedCarwashShop={selectedCarwashShop}
                      routeGeometry={routeGeometry}
                      stations={chargingStations}
                      selectedStation={selectedChargingStation}
                      source={carwashShopSource}
                      target={routeDestinationTarget}
                      vehicle={displayedVehicle}
                      onRefreshVehicleLocation={cockpitData.vehicle.refreshLocation}
                      onSelectCarwashShop={(shopId) => {
                        setSelectedCarwashShopId(shopId);
                        setCarwashShopManuallySelected(true);
                        setTraditionalCarwashFlowStage("selection");
                        setTraditionalCarwashCardCollapsed(false);
                      }}
                      onSelectStation={() => undefined}
                    />
                    <TraditionalDesktopCarwashNavigationCard
                      collapsed={traditionalCarwashCardCollapsed}
                      destinationName={destinationName}
                      destinationSearch={hasDestinationSearchRoute}
                      flowStage={traditionalCarwashFlowStage}
                      selectedShop={selectedCarwashShop}
                      selectedShopId={selectedCarwashShop.id}
                      shops={carwashShops}
                      onArriveCarwash={() => {
                        onCarwashArriveShop(selectedCarwashShop);
                      }}
                      onExpand={() => {
                        setTraditionalCarwashCardCollapsed(false);
                        if (traditionalCarwashFlowStage !== "arrivedCarwash") {
                          setTraditionalCarwashFlowStage("selection");
                        }
                      }}
                      onGoCarwash={() => {
                        setTraditionalCarwashFlowStage("toCarwash");
                        setTraditionalCarwashCardCollapsed(true);
                      }}
                      onSelectShop={(shopId) => {
                        setSelectedCarwashShopId(shopId);
                        setCarwashShopManuallySelected(true);
                        setTraditionalCarwashFlowStage("selection");
                        setTraditionalCarwashCardCollapsed(false);
                      }}
                    />
                  </>
                ) : null}
                {!isWaitingRouteVisible && shouldShowTraditionalRefuelCard ? (
                  <>
                    <TraditionalDesktopAmapLayer
                      destinationLocation={destinationLocation}
                      destinationName={destinationName}
                      mode="refuel"
                      fuelStations={fuelStations}
                      selectedFuelStation={selectedFuelStation}
                      routeGeometry={routeGeometry}
                      stations={chargingStations}
                      selectedStation={selectedChargingStation}
                      source={fuelStationSource}
                      target={routeDestinationTarget}
                      vehicle={displayedVehicle}
                      onRefreshVehicleLocation={cockpitData.vehicle.refreshLocation}
                      onSelectFuelStation={(stationId) => {
                        setSelectedFuelStationId(stationId);
                        setFuelStationManuallySelected(true);
                        setTraditionalRefuelFlowStage("selection");
                        setTraditionalRefuelCardCollapsed(false);
                      }}
                      onSelectStation={() => undefined}
                    />
                    <TraditionalDesktopRefuelNavigationCard
                      collapsed={traditionalRefuelCardCollapsed}
                      destinationName={destinationName}
                      destinationSearch={hasDestinationSearchRoute}
                      flowStage={traditionalRefuelFlowStage}
                      selectedStation={selectedFuelStation}
                      selectedStationId={selectedFuelStation.id}
                      stations={fuelStations}
                      onArriveRefuel={() => {
                        onRefuelArriveStation(selectedFuelStation);
                      }}
                      onExpand={() => {
                        setTraditionalRefuelCardCollapsed(false);
                        if (traditionalRefuelFlowStage !== "arrivedRefuel") {
                          setTraditionalRefuelFlowStage("selection");
                        }
                      }}
                      onGoRefuel={() => {
                        setTraditionalRefuelFlowStage("toRefuel");
                        setTraditionalRefuelCardCollapsed(true);
                      }}
                      onSelectStation={(stationId) => {
                        setSelectedFuelStationId(stationId);
                        setFuelStationManuallySelected(true);
                        setTraditionalRefuelFlowStage("selection");
                        setTraditionalRefuelCardCollapsed(false);
                      }}
                    />
                  </>
                ) : null}
                {!isWaitingRouteVisible && shouldShowTraditionalParcelCard ? (
                  <>
                    <TraditionalDesktopAmapLayer
                      destinationLocation={isParcelPresentationHomeRoute ? cockpitData.home.location : destinationLocation}
                      destinationName={isParcelPresentationHomeRoute ? cockpitData.home.name : destinationName}
                      mode="parcel"
                      homeLabel={cockpitData.home.name}
                      homeLocation={isParcelPresentationHomeRoute ? undefined : cockpitData.home.location}
                      parcelPoints={parcelPickupPoints}
                      selectedParcelPoint={selectedParcelPoint}
                      routeGeometry={routeGeometry}
                      stations={chargingStations}
                      selectedStation={selectedChargingStation}
                      source={parcelPointSource}
                      target={isParcelPresentationHomeRoute ? "restaurant" : "parcel"}
                      vehicle={displayedVehicle}
                      onRefreshVehicleLocation={parcelPresentation ? noop : cockpitData.vehicle.refreshLocation}
                      onSelectParcelPoint={(pointId) => {
                        setSelectedParcelPointId(pointId);
                        setTraditionalParcelFlowStage("selection");
                        setTraditionalParcelCardCollapsed(false);
                      }}
                      onSelectStation={() => undefined}
                    />
                    <TraditionalDesktopParcelNavigationCard
                      collapsed={traditionalParcelCardCollapsed}
                      flowStage={traditionalParcelFlowStage}
                      presentationMode={Boolean(parcelPresentation)}
                      pendingCount={cockpitData.parcel.pendingCount}
                      selectedPickupPoint={selectedParcelPoint}
                      onArrivePickup={() => {
                        setTraditionalParcelFlowStage("arrivedPickup");
                        setTraditionalParcelCardCollapsed(false);
                      }}
                      onArriveHome={() => {
                        onParcelArriveHome({
                          pendingCount: cockpitData.parcel.pendingCount || selectedParcelPoint.pendingCount,
                          pickupPointName: getPickupPointDisplayName(selectedParcelPoint)
                        });
                      }}
                      onExpand={() => {
                        setTraditionalParcelCardCollapsed(false);
                        if (traditionalParcelFlowStage !== "arrivedPickup" && traditionalParcelFlowStage !== "toHome") {
                          setTraditionalParcelFlowStage("selection");
                        }
                      }}
                      onGoHome={() => {
                        setTraditionalParcelFlowStage("toHome");
                        setTraditionalParcelCardCollapsed(true);
                      }}
                      onGoPickup={() => {
                        setTraditionalParcelFlowStage("toPickup");
                        setTraditionalParcelCardCollapsed(true);
                      }}
                      onOpenDetail={() => setParcelDetailOpen(true)}
                    />
                  </>
                ) : null}
              </div>
            ) : null}
            <NightStatusBar
              batteryLevel={state.batteryLevel}
              rangeKm={state.rangeKm}
              fuelLevel={state.fuelLevel}
              fuelRangeKm={state.fuelRangeKm}
              time={displayedTime}
              avatarSrc={avatarSrc}
              topDockSrc={topDockSrc}
            />
            <NightDock dockSrc={traditionalDockSrc} musicIconSrc={null} label="传统桌面底部 Dock" />
            {parcelDetailOpen ? (
              <ParcelDetailPopup
                demoPickup={parcelPresentation ? { pointName: getPickupPointDisplayName(selectedParcelPoint), pendingCount: cockpitData.parcel.pendingCount } : undefined}
                onClose={() => setParcelDetailOpen(false)}
              />
            ) : null}
            <NightVoiceCaptionOverlay evaSrc={evaFrontSrc} voiceCaption={voiceCaption} />
          </div>
        </CarScreenViewport>
      </main>
    );
  }

  return (
    <main className="desktop-stage desktop-stage--ai desktop-stage--figma-replica">
      <CarScreenViewport>
        <div className="desktop-stage__screen-canvas">
          {!isEvaSpaceVisible ? (
            <NightHeroLayer
              time={displayedTime}
              backgroundMode="night"
              wallpaperSrc={wallpaperSrc}
              evaSrc={evaFrontSrc}
              evaExpandedSrc={evaLookDownSrc}
              evaAlertSrc={evaAlertSrc}
              hasPrompt={hasNightPrompt}
              isCardExpanded={isNightCardExpanded}
            />
          ) : null}
          {isEvaSpaceVisible ? (
            <NightEvaSpaceDemo
              onClose={onEvaSpaceClose}
              onOpenAudioCompanion={onEvaSpaceOpenAudioCompanion}
              onOpenVideoCompanion={onEvaSpaceOpenVideoCompanion}
            />
          ) : null}
          <NightStatusBar
            batteryLevel={state.batteryLevel}
            rangeKm={state.rangeKm}
            fuelLevel={state.fuelLevel}
            fuelRangeKm={state.fuelRangeKm}
            time={displayedTime}
            avatarSrc={avatarSrc}
            topDockSrc={topDockSrc}
          />
          {!isEvaSpaceVisible ? <NightFloatingShortcut shortcutSrc={floatingShortcutSrc} /> : null}
          {isAudioCompanionVisible && audioCompanionDemo ? (
            <NightAudioCompanionDemo
              state={audioCompanionDemo}
              onAnswerFinished={onAudioCompanionAnswerFinished}
              onBackToRecommend={onAudioCompanionBackToRecommend}
              onExpandPreview={onAudioCompanionExpandPreview}
              onFallbackSpeak={onAudioCompanionFallbackSpeak}
              onPreviewProgram={onAudioCompanionPreviewProgram}
              onSelectProgram={onAudioCompanionSelectProgram}
              onStartGeneratedProgram={onAudioCompanionStartGeneratedProgram}
            />
          ) : null}
          {isVideoCompanionVisible && videoCompanionDemo ? (
            <NightVideoCompanionLayer
              evaSrc={evaFrontSrc}
              state={videoCompanionDemo}
              onActorFinished={onVideoCompanionActorFinished}
              onAskActor={onVideoCompanionAskActor}
              onOpenQuicklook={onVideoCompanionOpenQuicklook}
              onPlaybackEnded={onVideoCompanionPlaybackEnded}
              onReset={onVideoCompanionReset}
              onSelectVideo={onVideoCompanionSelectVideo}
            />
          ) : null}
          {isMovieGachaVisible ? (
            <NightMovieGachaDemo evaSrc={evaFrontSrc} onClose={onMovieGachaClose} />
          ) : null}
          {isTomatoReactionVisible ? (
            <NightTomatoReactionDemo evaSrc={evaFrontSrc} onClose={onTomatoReactionClose} />
          ) : null}
          {isXiaohongshuParkingVisible ? <NightXiaohongshuParkingDemo /> : null}
          {!isCompanionVisible && !accidentGuideOpen && !selectedDecision && !isCarwashModeVisible && !isChargingSessionVisible && !isParcelExitReminderVisible && !isRefuelAuthorizationVisible ? (
            <NightAiCard
              summary={summary}
              selectedService={selectedService}
              carwash={cockpitData.carwash}
              chargingStation={selectedChargingStation}
              chargingStationCount={chargingStations.length}
              chargingStationSource={chargingStationSource}
              destinationName={destinationName}
              fuel={cockpitData.fuel}
              parcel={cockpitData.parcel}
              parcelDescription={parcelPresentation ? "查看家／公司附近的待取快递" : undefined}
              onOpenService={onOpenService}
            />
          ) : null}
          {!isCompanionVisible && selectedDecision && !accidentGuideOpen && !isCarwashModeVisible && !isChargingSessionVisible && !isRefuelAuthorizationVisible ? (
            <NightAiExpandedCard
              decision={selectedDecision}
              state={state}
              carwash={cockpitData.carwash}
              chargingStation={selectedChargingStation}
              chargingStationCount={chargingStations.length}
              chargingStationSource={chargingStationSource}
              destinationName={destinationName}
              fuel={cockpitData.fuel}
              parcel={cockpitData.parcel}
              parkingPaymentOpen={parkingPaymentOpen}
              onClose={onCloseService}
              onConfirm={onConfirmService}
              onCancelParkingPayment={onCloseParkingPayment}
              onOpenParcelDetail={() => setParcelDetailOpen(true)}
              onParkingPaymentPaid={onParkingPaymentPaid}
            />
          ) : null}
          {!isCompanionVisible && isParcelExitReminderVisible && !accidentGuideOpen && !selectedDecision && !isCarwashModeVisible && !isChargingSessionVisible && !isRefuelAuthorizationVisible ? (
            <NightParcelExitReminderCard
              reminder={parcelExitReminder}
              onDismiss={onDismissParcelExitReminder}
              onOpenDetail={() => setParcelDetailOpen(true)}
            />
          ) : null}
          {!isCompanionVisible && isChargingSessionVisible && !accidentGuideOpen ? (
            <NightChargingSessionCard
              session={chargingSession}
              onConfirmCover={onConfirmChargingCover}
              onDismiss={onDismissChargingSession}
              onEnableRestMode={onEnableChargingRestMode}
              onShowRestRecommendation={onShowChargingRestRecommendation}
            />
          ) : null}
          {!isCompanionVisible && isCarwashModeVisible && !accidentGuideOpen && !selectedDecision && !isChargingSessionVisible ? (
            <NightCarwashModeCard
              session={carwashMode}
              onConfirm={onConfirmCarwashMode}
              onDismiss={onDismissCarwashMode}
            />
          ) : null}
          {!isCompanionVisible && isRefuelAuthorizationVisible && !accidentGuideOpen && !selectedDecision && !isChargingSessionVisible ? (
            <NightRefuelAuthorizationCard
              session={refuelAuthorization}
              onAuthorize={onAuthorizeRefuel}
              onDismiss={onDismissRefuelAuthorization}
            />
          ) : null}
          {!isEvaSpaceVisible && !accidentGuideOpen && !isChargingSessionVisible ? (
            <NightBottomCards parkingCardSrc={parkingCardSrc} musicCardSrc={musicCardSrc} />
          ) : null}
          <NightDock />
          {accidentGuideOpen ? (
            <NightAccidentGuideOverlay
              callVisible={accidentCallVisible}
              evaSrc={evaLookDownSrc}
              onConfirmSafe={onConfirmAccidentGuide}
              onHangupCall={onHangupAccidentCall}
            />
          ) : null}
          {parcelDetailOpen ? (
            <ParcelDetailPopup
              demoPickup={parcelPresentation ? { pointName: getPickupPointDisplayName(selectedParcelPoint), pendingCount: cockpitData.parcel.pendingCount } : undefined}
              onClose={() => setParcelDetailOpen(false)}
            />
          ) : null}
          <NightVoiceCaptionOverlay evaSrc={evaFrontSrc} voiceCaption={voiceCaption} />
        </div>
      </CarScreenViewport>
    </main>
  );
}

function createStationVehicleState(
  vehicle: CockpitData["vehicle"],
  location: GeoPoint
) {
  return {
    ...vehicle,
    vehicleLocation: location,
    locationStatus: "demo" as const,
    locationSource: "demo" as const,
    locationAccuracyM: undefined,
    locationError: undefined
  };
}

function getTraditionalNavigationSrc({
  activeServiceId,
  carwashNavigationSrc,
  chargingNavigationSrc,
  hasCarwashInput,
  hasChargingRoute,
  hasParcelRoute,
  parcelNavigationSrc
}: {
  activeServiceId: ServiceId | null;
  carwashNavigationSrc: string;
  chargingNavigationSrc: string;
  hasCarwashInput: boolean;
  hasChargingRoute: boolean;
  hasParcelRoute: boolean;
  parcelNavigationSrc: string;
}) {
  if (activeServiceId === "parcel" && hasParcelRoute) {
    return parcelNavigationSrc;
  }

  if (activeServiceId === "charging" && hasChargingRoute) {
    return chargingNavigationSrc;
  }

  if (activeServiceId === "carwash" && hasCarwashInput) {
    return carwashNavigationSrc;
  }

  if (hasParcelRoute) {
    return parcelNavigationSrc;
  }

  if (hasCarwashInput) {
    return carwashNavigationSrc;
  }

  if (hasChargingRoute) {
    return chargingNavigationSrc;
  }

  return null;
}
